import ProcessingResult from "../../framework/ProcessingResult";

import PathAIApi from "../../api/PathAIApi";

import PathParameterDto from "../../classes/dtos/pathcommonmodule/PathParameterDto";

import { JobStatusCanceled, JobStatusError, JobStatusTerminated } from "../../classes/consts/JobStatus";

export class PathAIPageRows {
    AgentUrl: string;
    TaskId: string;
    NextPage: string;
    Rows: any[];

    constructor(agentUrl: string, taskId: string, nextPage: string, rows: any[]) {
        this.AgentUrl = agentUrl;
        this.TaskId = taskId;
        this.NextPage = nextPage;
        this.Rows = rows;
    }
}

export interface PathAIRowsOptions {
    PollingIntervalMs?: number;
    TimeoutMs?: number;
    Signal?: AbortSignal;
}

const DefaultPollingIntervalMs = 500;
const DefaultTimeoutMs = 60 * 1000;

export const PathAIRowsAborted = "Aborted";
export const PathAIRowsTimeout = "Timeout";

export default class PathAIRowsLogic {

    // Reads the first page of rows: creates the request on the main API, then polls the agent until the task is terminated.
    GetFirstPageRowsAsync = async (pathId: number, fieldReferenceIds: number[] | null, parameters: PathParameterDto[], filterParameters: string | null, options: PathAIRowsOptions = {}): Promise<ProcessingResult<PathAIPageRows>> => {
        try {
            let createResult = await PathAIApi.CreateRequestPathAIAsync(pathId, fieldReferenceIds, parameters, filterParameters, options.Signal);
            if (createResult.IsFailed()) {
                return ProcessingResult.Error("Error in CreateRequestPathAIAsync " + createResult.ErrorMessage);
            }

            if (!createResult.Result.Accepted) {
                return ProcessingResult.Error("Request not accepted");
            }

            return await this.WaitPageAsync(createResult.Result.Url, createResult.Result.Token, "", options);
        }
        catch (error) {
            return this.ToError(error);
        }
    }

    // Reads the following page, using the AgentUrl, TaskId and NextPage of the previous page.
    GetNextPageRowsAsync = async (previousPage: PathAIPageRows, options: PathAIRowsOptions = {}): Promise<ProcessingResult<PathAIPageRows>> => {
        if (previousPage.NextPage === "") {
            return ProcessingResult.Ok(new PathAIPageRows(previousPage.AgentUrl, previousPage.TaskId, "", []));
        }

        try {
            return await this.WaitPageAsync(previousPage.AgentUrl, previousPage.TaskId, previousPage.NextPage, options);
        }
        catch (error) {
            return this.ToError(error);
        }
    }

    // Reads all the pages and returns every row.
    GetAllRowsAsync = async (pathId: number, fieldReferenceIds: number[] | null, parameters: PathParameterDto[], filterParameters: string | null, options: PathAIRowsOptions = {}): Promise<ProcessingResult<any[]>> => {
        let pageResult = await this.GetFirstPageRowsAsync(pathId, fieldReferenceIds, parameters, filterParameters, options);
        if (pageResult.IsFailed()) {
            return ProcessingResult.Error(pageResult.ErrorMessage);
        }

        let rows: any[] = [...pageResult.Result.Rows];

        while (pageResult.Result.NextPage !== "") {
            pageResult = await this.GetNextPageRowsAsync(pageResult.Result, options);
            if (pageResult.IsFailed()) {
                return ProcessingResult.Error(pageResult.ErrorMessage);
            }

            rows.push(...pageResult.Result.Rows);
        }

        return ProcessingResult.Ok(rows);
    }

    private WaitPageAsync = async (agentUrl: string, taskId: string, pageToken: string, options: PathAIRowsOptions): Promise<ProcessingResult<PathAIPageRows>> => {
        const pollingIntervalMs = options.PollingIntervalMs ?? DefaultPollingIntervalMs;
        const deadline = Date.now() + (options.TimeoutMs ?? DefaultTimeoutMs);

        do {
            let getResult = await PathAIApi.GetRequestPathAIAsync(agentUrl, taskId, pageToken, options.Signal);
            if (getResult.IsFailed()) {
                return ProcessingResult.Error("Error in GetRequestPathAIAsync " + getResult.ErrorMessage);
            }

            switch (getResult.Result.Status) {
                case JobStatusTerminated:
                    return ProcessingResult.Ok(new PathAIPageRows(agentUrl, taskId, getResult.Result.NextPage, getResult.Result.Rows));
                case JobStatusError:
                    return ProcessingResult.Error("PathAI execution failed");
                case JobStatusCanceled:
                    return ProcessingResult.Error("PathAI execution canceled");
            }

            await this.DelayAsync(pollingIntervalMs, options.Signal);
        }
        while (Date.now() < deadline);

        return ProcessingResult.Error(PathAIRowsTimeout);
    }

    private DelayAsync = (ms: number, signal?: AbortSignal): Promise<void> => {
        return new Promise<void>((resolve, reject) => {
            if (signal?.aborted) {
                reject(new DOMException("Aborted", "AbortError"));
                return;
            }

            const onAbort = () => {
                clearTimeout(timer);
                reject(new DOMException("Aborted", "AbortError"));
            };

            const timer = setTimeout(() => {
                signal?.removeEventListener("abort", onAbort);
                resolve();
            }, ms);

            signal?.addEventListener("abort", onAbort, { once: true });
        });
    }

    private ToError = (error: unknown): ProcessingResult<any> => {
        if (error instanceof DOMException && error.name === "AbortError") {
            return ProcessingResult.Error(PathAIRowsAborted);
        }

        return ProcessingResult.Error("Unexpected error " + (error instanceof Error ? error.message : String(error)));
    }
}
